import { useParams } from 'react-router';
import {
  SitrepSectionType,
  useSitrepV2,
  useUpdateSitrepSectionV2,
} from '../DisasterGPT/hooks/useSitrepsV2';
import PageTitle from '../headers/PageTitle';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import dayjs from 'dayjs';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import { useToggle } from 'react-use';
import SitrepSectionWizard from './SitrepSectionWizard/SitrepSectionWizard';
import MDEditor from '@uiw/react-md-editor';
import React, { useState } from 'react';
import { Components } from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { AcknowledgmentStatusAlerts } from '../DisasterGPTAlerts/components/AcknowledgmentStatusAlerts';

// Pulled from SitrepSection.js
const markdownComponents: Components = {
  table: ({ node: _, ...props }) => (
    <table className="custom-markdown-table" {...props} />
  ),
  th: ({ node: _, ...props }) => (
    <th className="custom-markdown-th" {...props} />
  ),
  td: ({ node: _, ...props }) => (
    <td className="custom-markdown-td" {...props} />
  ),
  tr: ({ node: _, ...props }) => (
    <tr className="custom-markdown-tr" {...props} />
  ),
  a: ({ node: _, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
};

type SitrepSectionProps = {
  section: SitrepSectionType;
  sitrepId: UUID;
};
const SitrepSection = ({ section, sitrepId }: SitrepSectionProps) => {
  const [isEditing, setIsEditing] = useToggle(false);
  const { mutate } = useUpdateSitrepSectionV2();
  const sectionText =
    section.section_versions[0].sitrep_section_text ?? undefined;
  const [localSectionText, setLocalSectionText] = useState<string | undefined>(
    sectionText
  );

  const handleEditButtonClick = () => {
    if (
      isEditing &&
      localSectionText !== section.section_versions[0].sitrep_section_text
    ) {
      // Save changes
      mutate({
        sitrepId,
        sectionId: section.id,
        updatedFields: { sectionText: localSectionText ?? '' },
      });
    }
    setIsEditing();
  };

  return (
    <div className="sitrep-section">
      <div className="sitrep-section-header d-flex flex-row justify-content-between">
        <p className="sitrep-section-header-text align-self-center">
          {`Reported Event: ${section.name} | ${dayjs(section.section_versions[0].timestamp)}`}
        </p>
        <StylishNewButton
          className="button--icon"
          onClick={handleEditButtonClick}
        >
          <SharedIcon iconName="edit" color="light" />
        </StylishNewButton>
      </div>
      <div className="sitrep-section-content">
        {isEditing ? (
          <MDEditor
            value={localSectionText}
            onChange={(value) => setLocalSectionText(value)}
            height="100%"
          />
        ) : (
          sectionText && (
            <MDEditor.Markdown
              source={sectionText}
              components={markdownComponents}
              rehypePlugins={[[rehypeSanitize]]}
            />
          )
        )}
      </div>
    </div>
  );
};

const noop = () => {};
const SingleSitrep = () => {
  const { sitrepId } = useParams();
  const { data: sitrep, isLoading, isError, isSuccess } = useSitrepV2(sitrepId);
  const [isEditingSitrepSections, setIsEditingSitrepSections] =
    useToggle(false);

  // Alert acknowledgements logic
  const isAlertSitrep = sitrep?.sitrep_source === 'ALERT';
  const alertEventInstanceId = sitrep?.sitrep_source_id;

  if (isLoading) {
    return <>Loading...</>;
  }

  if (isError || !isSuccess) {
    return <>Error loading Sitrep</>;
  }

  const sortedSitrepSections = sitrep.sections.sort(
    (a, b) => a.order - b.order
  );

  return (
    <div className="site-main">
      <div className="d-flex flex-row w-100">
        <div className="container-fluid p-3">
          <PageTitle title={sitrep.name} parents={['Sitreps']} />

          {/* Alert Acknowledgment Status */}
          <AcknowledgmentStatusAlerts
            isAlertSitrep={isAlertSitrep}
            eventInstanceId={alertEventInstanceId}
            showSummary={true}
          />

          <div className="d-flex gap-3">
            <StylishNewButton
              className="button--primary color-black"
              onClick={setIsEditingSitrepSections}
            >
              Edit Report
            </StylishNewButton>
            <StylishNewButton
              className="button--primary color-black"
              onClick={noop}
            >
              Download
            </StylishNewButton>
            <StylishNewButton
              className="button--primary color-black"
              onClick={noop}
            >
              Create Incident
            </StylishNewButton>
          </div>
          <div className="d-flex flex-column gap-3 mt-3">
            {Array.isArray(sortedSitrepSections) &&
              sortedSitrepSections.length > 0 &&
              sortedSitrepSections.map((section) => (
                <SitrepSection
                  key={section.id}
                  section={section}
                  sitrepId={sitrep.id}
                />
              ))}
          </div>
        </div>
        {isEditingSitrepSections && (
          <SitrepSectionWizard
            setIsEditingSitrepSections={setIsEditingSitrepSections}
          />
        )}
      </div>
    </div>
  );
};

export default SingleSitrep;
